
import { useState } from 'react';
import acquirersData from '@/data/acquirers.json';
import { ConnectedAccount } from './types';
import { useAcquirers } from '@/contexts/AcquirersContext';
import { useActivation } from '@/contexts/ActivationContext';
import ConnectedAccountCard from './ConnectedAccountCard';
import AddConnectionForm from './AddConnectionForm';
import EmptyConnectionsState from './EmptyConnectionsState';
import DeleteConfirmModal from './DeleteConfirmModal';
import EditConnectionForm from './EditConnectionForm';
import { httpClient } from '@/lib/http-client';
import { getEndpointUrl } from '@/lib/api-config';

const acquirers = acquirersData.acquirers;

export default function CredencialesAdquirentePanel() {
  const { connections, isLoading, error, addConnection, removeConnection, updateConnection } = useAcquirers();
  const { refreshStatus } = useActivation();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [dispatchingId, setDispatchingId] = useState<string | null>(null);
  const [dispatchSuccessId, setDispatchSuccessId] = useState<string | null>(null);

  const handleConnect = (account: ConnectedAccount) => {
    addConnection(account);
    setShowAddForm(false);
  };

  const handleEditToggle = (id: string) => {
    setEditingId((prev) => (prev === id ? null : id));
  };

  const handleSave = async (id: string, fields: Record<string, string>) => {
    setSavingId(id);
    try {
      const urlTemplate = getEndpointUrl('acquirerConnections', 'update');
      const url = urlTemplate.replace('{id}', id);
      await httpClient.put(url, { rut: fields.rut, password: fields.password });
      updateConnection(id, fields);
      setEditingId(null);
      await refreshStatus();
    } catch {
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteRequest = (id: string) => {
    setDeletingId(id);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingId) return;
    setIsDeleting(true);
    try {
      const urlTemplate = getEndpointUrl('acquirerConnections', 'delete');
      const url = urlTemplate.replace('{id}', deletingId);
      await httpClient.delete(url);
      removeConnection(deletingId);
      setDeletingId(null);
      await refreshStatus();
    } catch {
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (isDeleting) return;
    setDeletingId(null);
  };

  const handleDispatchWorker = async (id: string) => {
    const account = connections.find((c) => c.id === id);
    if (!account) return;
    setDispatchingId(id);
    setDispatchSuccessId(null);
    try {
      const urlTemplate = getEndpointUrl('acquirerConnections', 'workerDispatch');
      const url = urlTemplate.replace('{acquirerId}', account.acquirerId);
      await httpClient.post(url, {});
      setDispatchSuccessId(id);
      setTimeout(() => setDispatchSuccessId((prev) => (prev === id ? null : prev)), 3000);
    } catch {
    } finally {
      setDispatchingId(null);
    }
  };

  const deletingAccount = connections.find((c) => c.id === deletingId);
  const existingAcquirerIds = connections.map((c) => c.acquirerId);
  const hasConnections = connections.length > 0;

  return (
    <>
      {deletingId && deletingAccount && (
        <DeleteConfirmModal
          acquirerName={deletingAccount.acquirerName}
          isDeleting={isDeleting}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}

      <div className="mx-auto w-full max-w-2xl bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden min-h-[600px]">
        <div className="p-6 md:p-8">
          <div className="mb-8 border-b border-slate-100 dark:border-slate-800 pb-6">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              Credenciales Adquirente
            </h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              {hasConnections
                ? 'Gestiona tu conexión con tu proveedor de pagos.'
                : 'Gestiona la conexión con tu proveedor de pago para automatizar la recaudación.'}
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <span className="material-symbols-outlined text-3xl text-slate-300 animate-spin">progress_activity</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                <span className="material-symbols-outlined text-2xl text-red-500">error</span>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{error}</p>
            </div>
          ) : !hasConnections ? (
            <div>
              <EmptyConnectionsState />

              <div className="border-t border-slate-100 dark:border-slate-800 pt-8 space-y-8">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">add_circle</span>
                  Agregar nueva conexión
                </h3>

                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-6 md:p-8 border border-slate-200 dark:border-slate-700">
                  <AddConnectionForm
                    acquirers={acquirers}
                    existingIds={existingAcquirerIds}
                    onConnect={handleConnect}
                  />
                </div>

                <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 p-4 border border-blue-100 dark:border-blue-800">
                  <div className="flex gap-3">
                    <span className="material-symbols-outlined text-primary flex-shrink-0">info</span>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      ¿No recuerdas tus credenciales? Puedes recuperarlas directamente en el portal de
                      comercios de tu proveedor.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-10">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                  Tus Conexiones
                </h3>
                <div className="grid gap-4">
                  {connections.map((account) => {
                    const editing = editingId === account.id;
                    const acquirer = acquirers.find((a) => a.id === account.acquirerSlug);
                    return (
                      <div key={account.id}>
                        <ConnectedAccountCard
                          account={account}
                          isEditing={editing}
                          isDispatching={dispatchingId === account.id}
                          dispatchSuccess={dispatchSuccessId === account.id}
                          onEdit={handleEditToggle}
                          onDelete={handleDeleteRequest}
                          onDispatchWorker={handleDispatchWorker}
                        />
                        {editing && acquirer && (
                          <EditConnectionForm
                            account={account}
                            acquirer={acquirer}
                            isSaving={savingId === account.id}
                            onSave={(fields) => handleSave(account.id, fields)}
                            onCancel={() => setEditingId(null)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="border-t border-slate-200 dark:border-slate-800" />

              <div>
                <button
                  onClick={() => setShowAddForm((v) => !v)}
                  type="button"
                  className="group flex items-center gap-2 text-primary font-semibold hover:text-blue-700 dark:hover:text-blue-400 transition-colors mb-6"
                >
                  <span className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <span className="material-symbols-outlined text-xl">
                      {showAddForm ? 'remove' : 'add'}
                    </span>
                  </span>
                  {showAddForm ? 'Cancelar' : 'Agregar otra conexión'}
                </button>

                {showAddForm && (
                  <div className="space-y-6 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-6 md:p-8 border border-slate-200 dark:border-slate-700">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                      Nueva Conexión
                    </h3>
                    <AddConnectionForm
                      acquirers={acquirers}
                      existingIds={existingAcquirerIds}
                      onConnect={handleConnect}
                      onCancel={() => setShowAddForm(false)}
                      showCancel
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
